import React, { useEffect, useRef } from 'react';
import Highcharts from 'highcharts';
import { map } from 'lodash';
import { useScreenBreakpoint } from 'consts/breakpoints';
import { useTheme } from 'hooks/useTheme';
import { useLocale } from 'locale';
import { IInvestorsHistory } from 'store/sagas/apiService/types';

interface DealsHistoryChartProps {
  years: string[];
  newSales: number[];
  oldSales: number[];
  // 'ticker' = the v2 Investors home MarketTrendTicker dropdown. Chart styling tweaks are scoped
  // to this variant so the shared v1 chart is never affected; the dashed-gridline / mobile look
  // is additionally gated to the mobile breakpoint below.
  variant?: 'default' | 'ticker';
}

export const toChartSeries = (historyData: IInvestorsHistory[]) => ({
  years: map(historyData, el => el.year.toString()),
  oldSales: map(historyData, 'oldPropertySales'),
  newSales: map(historyData, 'newPropertySales'),
});

export const DealsHistoryChart: React.FC<DealsHistoryChartProps> = props => {
  const { years, newSales, oldSales, variant } = props;
  const containerRef = useRef(null);
  const theme = useTheme();
  const { formatNumber, t } = useLocale();
  const screen = useScreenBreakpoint();
  const isDevice = screen < 3;
  const isTicker = variant === 'ticker';
  const isTickerMobile = isTicker && isDevice;

  // v2 ("ticker") renders chart text at the design-system small_text size (14px); v1 keeps its
  // legacy 13px and its default (un-styled) axis labels.
  const chartFontSize = isTicker ? '14px' : '13px';
  const tickerAxisLabels = {
    style: {
      color: theme.colors.neutrals.grey1,
      fontFamily: 'NovemberHebrew',
      fontSize: '14px',
    },
  };

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const chart = Highcharts.chart(containerRef.current, {
      chart: { type: 'column', height: 308 },
      colors: [ '#015E61', '#14C17B', '#e71d36', '#011627' ],
      title: { text: null },
      subtitle: null,
      legend: {
        rtl: true,
        squareSymbol: true,
        symbolRadius: 3,
        symbolWidth: 16,
        symbolHeight: 16,
        align: 'right',
        verticalAlign: 'bottom',
        layout: 'horizontal',
        useHTML: true,
        itemStyle: {
          color: theme.colors.neutrals.grey1,
          fontFamily: 'NovemberHebrew',
          fontSize: chartFontSize,
          fontWeight: 'normal',
        },

      },
      xAxis: {
        categories: years,
        crosshair: true,
        ...(isTicker ? { labels: tickerAxisLabels } : {}),
      },
      tooltip: {
        shared: true,
        useHTML: true,
        backgroundColor: '#303030',
        borderWidth: 0,
        borderRadius: 16,
        padding: 16,
        style: {
          padding: '8px 226px 8px 24px',
          color: '#ffffff',
          fontFamily: 'Novemberhebrew',
          fontSize: chartFontSize,
        },
        formatter() {
          const header =
            `<div style="color:#B2B2B2;font-weight:bold;text-align:right">
              ${this.x}
            </div>`;

          const rows = this.points
            .map(p => {
              return (
                '<div style="display:flex;align-items:center;gap:6px">' +
                  `<span style="
                    width:12px;height:12px;border-radius:2px;
                    background:${p.color};
                  "></span>` +
                  `<span dir="ltr">${formatNumber(p.y, false)}</span>` +
                '</div>'
              );
            })
            .join('');

          return (
            '<div dir="rtl" style="display:flex;flex-direction:column;gap:8px">' +
              header + rows +
            '</div>'
          );
        },
      },
      yAxis: {
        min: 0,
        title: null,
        ...(isTicker ? { labels: tickerAxisLabels } : {}),
        // mobile ticker chart (Figma node 11164-94951): light dashed horizontal gridlines.
        ...(isTickerMobile ? {
          gridLineDashStyle: 'Dash',
          gridLineColor: theme.colors.neutrals.grey8,
        } : {}),
      },
      plotOptions: { column: { pointWidth: isTickerMobile ? 18 : (isDevice ? 13 : 30), pointPadding: isDevice ? 0 : 10, groupPadding: isDevice ? 0.15 : 0.1 } },
      series: [
        // Figma legend (node 11164-94828): new apartments = teal (#015E61), second-hand = green
        // (#14C17B). Pin the colors explicitly for the ticker variant so the mapping can't flip.
        { name: t('investors.dealsHistory.newApartments'),  data: newSales, index: 1, legendIndex: 0, ...(isTicker ? { color: '#015E61' } : {}) },
        { name: t('investors.dealsHistory.oldApartments'),  data: oldSales, index: 0, legendIndex: 1, ...(isTicker ? { color: '#14C17B' } : {}) },
      ],
      credits: { enabled: false },
    } as Highcharts.Options);

    return () => chart && chart.destroy();
    // callers build years/newSales/oldSales inline (new array refs every render), so key the rebuild
    // on their joined values — not identity — to re-render on real data/variant/breakpoint/theme
    // changes without destroying+recreating the chart on every parent render.
  }, [ years.join(','), newSales.join(','), oldSales.join(','), variant, isDevice, theme ]);

  return <div ref={containerRef} data-testid="corn-wheat-chart" />;
};
